import { useEffect, useRef } from 'react';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useTranslation } from 'react-i18next';
import ErrorPage from './ErrorPage';
import { useLocation } from 'react-router-dom';
import { useRoutes } from '../../hooks/useRoutes';

const TRANSLATION_KEY = 'ERRORS.AUTO_SIGNED_OFF.';

const REDIRECT_TIME = 5000;

/**
 *  This page will be rendered by <ErrorBoundary /> when a RefreshTokenFailedError is thrown
 *
 * This can be force by making changes in BearerToken.ts:
 * set TOKEN_GRACE_PERIOD_IN_SECONDS its value to 999999999
 * throw following object in the then() callback of the /oauth/refresh_token: {response: {status: 401}}
 */
export default function AutoSignedOff() {
  const { t } = useTranslation();

  useDocumentTitle(t(`${TRANSLATION_KEY}DOCUMENT_TITLE`));

  const { pathname, search } = useLocation();
  const { buildAbsolutePath } = useRoutes();

  const loginPath = buildAbsolutePath(['LOGIN']);
  const returnUrl = pathname.startsWith(loginPath)
    ? new URLSearchParams(search).get('returnUrl')
    : [pathname, search].join('');
  const loginUrl = returnUrl ? `${loginPath}?returnUrl=${encodeURIComponent(returnUrl)}` : loginPath;

  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    timeoutRef.current = window.setTimeout(() => window.location.assign(loginUrl), REDIRECT_TIME);

    return () => window.clearTimeout(timeoutRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ErrorPage
      headerText={t(`${TRANSLATION_KEY}HEADING`)}
      message={t(`${TRANSLATION_KEY}MESSAGE`)}
      link={{
        text: t(`${TRANSLATION_KEY}ACTION`),
        to: loginUrl,
      }}
    />
  );
}
